import { toast } from "react-toastify";
import { apiClient as flexFocusApiClient } from "../../../services/apiClient";
import { throwError } from "../../../utils/errorHandling";
import {
  IOrderHead,
  IResponse,
  IOrderStyle,
  IObjectDataResponse,
  IOrderheadInput,
  IMutationResponse,
  IYarnDetailsInput,
  IStyleAccessory,
  IMutationStyleAccResponse,
  IStyleAccessoryInput,
  IStyleAccessoryResponse,
  IStylePhotoResponse,
  ITimeline,
  ITimelineInput,
  ITimelineCreateResponse,
  IStandardResponse,
  TimelineData,
} from "../models/order";
import { elasticSearchClient } from "../../../services/elacticSearchClient";
import { TOrderItem } from "../../raw-material/models/rmm_models";
import { ICreateYarnComposition, IUpdateYarnComposition } from "../../user/model/gestion";
export interface ISampleRef {
  sample_id: string;
  reference_affichee: string;
  sample_reference_affichee: string;
  client: string | null;
  type_sample: string | null;
  style: string | null;
  date_emission: string | null;
  date_demandee: string | null;
  statut: string | null;
  costing_ref: string | null;
  costing_our_sample_ref: string | null;
  order_number: string | null;
  costing_style_ref: string | null;
  factory_cmt_A: string | null;
  devise_costing: string | null;
  marge_A_pourcent: string | null;
  prix_vente_A_enregistre: string | null;
  cmt_id: string | null;
  prix_cmt_usd: string | null;
  operation_cmt: string | null;
  usine_cmt: string | null;
  statut_prix_A: string;
}

export interface IOrderClient {
  orderhead_id: number;
  order_code: string;
  client_id: number | null;
  client_name: string | null;
}

export enum EOrderEndPoint {
  ORDER_HEAD = "orderhead/orderall",
  ORDER_STYLE = "orderhead/orderBystyle",
  ONE_ORDER_HEAD = "orderhead/order",
  ONE_ORDER_STYLE = "style/show",
  CREATE_ORDERHEAD = "orderhead/create",
  UPDATE_ORDERHEAD = "orderhead/update/",
  ADD_YARN_DETAILS = "orderhead/addYarnDetail/",
  UPDATE_YARN_DETAILS = "orderhead/updateYarnDetail/",
  CREATE_STYLE_ACCESSORY = "accessory/create/",
  STYLE_ACCESSORY = "accessory/all_accessory",
  UPDATE_STYLE_ACCESSORY = "accessory/edit_accessory/",
  DELETE_STYLE_ACCESSORY = "accessory/delete_accessory/",
  STYLE_PHOTOS = "photos/allPhotos",
  CREATE_STYLE_PHOTOS = "photos/upload/",
  DELETE_STYLE_PHOTO = "photos/",
  STYLE_TIMELINE = "timeline/all/",
  CREATE_TIMELINE = "timeline/create/",
  UPLOAD_TIMELINE = "timeline/upload/",
  ORDER_HEAD_SEARCH = "orderhead/list/?search=",
  ORDER_CLIENTS = "data-provider/order-clients",
  TRACKING_LOG="orderhead/log/",
  STYLE_TRACKING_LOG="style/log/",
  SEARCH_SAMPLEREF="data-provider/sample_ref?search=",
  PDF_OC="orderhead/generateOrderConfirmation/",
  CONFIRM_ORDER="orderhead/confirm",
  DELETE_ORDER="orderhead/delete",
  REOPEN_ORDER="orderhead/reopen",
  ORDER_TIMELINE="orderhead/timeline",
  EXCEL_IMPORT_UPLOAD = "/excel-import/upload",
  EXCEL_IMPORT_PROGRESS = "/excel-import/progress",
}

export enum EExcelImportType {
  YARN_COUNT = 'yarn_count',
  YARN_COMPOSITION = 'yarn_composition',
}

export const orderApi = {
  getOrderClientsByCodes: async (codes: string[]): Promise<IOrderClient[]> => {
    try {
      const { data } = await flexFocusApiClient.get(
        EOrderEndPoint.ORDER_CLIENTS,
        {
          params: { codes: codes.join(",") },
        }
      );
      return (data as any)?.data ?? data ?? [];
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getOrderHeadSearch: async (key: string): Promise<IStandardResponse<Array<IOrderHead>>> => {
    try {
      const { data } = await flexFocusApiClient.get<IStandardResponse<Array<IOrderHead>>>(
        EOrderEndPoint.ORDER_HEAD_SEARCH + key
      );
      return {
      ...data,
      data: (data as any).data?.data ?? [],
      };
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getOrderHead: async (
    type: TOrderItem,
    page: number = 1,
    limit: number = 10,
    search: Record<string, any>,
    sort: string,
    global_search?: string
  ): Promise<IResponse<IOrderHead>> => {
    try {
      const { data } = await flexFocusApiClient.get<IResponse<IOrderHead>>(
        EOrderEndPoint.ORDER_HEAD,
        {
          params: {
            type,
            page,
            limit,
            search,
            sort,
            global_search,
          },
        }
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getOrdersStyle: async (
    type: TOrderItem,
    page: number = 1,
    limit: number = 10,
    search: Record<string, any>,
    sort?: string,
    global_search?: string
  ): Promise<IResponse<IOrderStyle>> => {
    try {
      const { data } = await flexFocusApiClient.get<IResponse<IOrderStyle>>(
        EOrderEndPoint.ORDER_STYLE,
        {
          params: {
            type,
            page,
            limit,
            search,
            sort,
            global_search,
          },
        }
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllStyles: async (
    styles:string
  ):  Promise<IResponse<IOrderStyle>>  => {
    const controller = new AbortController();
    const timeout = 30_000;
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);
    try {
      const { data } = await flexFocusApiClient.get<IResponse<IOrderStyle>>(
        `${EOrderEndPoint.ORDER_STYLE}?search[orl.style_code]=contains,${styles}`,{
          signal:controller.signal,
        }
      );
      clearTimeout(timeoutId);
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      throw new Error(throwError(error));
    }
  },
  getOneOrderHead: async (
    id: number
  ): Promise<IObjectDataResponse<IOrderHead>> => {
    try {
      const { data } = await flexFocusApiClient.get<
        IObjectDataResponse<IOrderHead>
      >(EOrderEndPoint.ONE_ORDER_HEAD + "/" + id);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getOneOrderStyle: async (
    id: number
  ): Promise<IObjectDataResponse<IOrderStyle>> => {
    try {
      const { data } = await flexFocusApiClient.get<
        IObjectDataResponse<IOrderStyle>
      >(EOrderEndPoint.ONE_ORDER_STYLE + "/" + id);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  createOrderhead: async (
    orderhead: IOrderheadInput
  ): Promise<IMutationResponse> => {
    try {
      const { data } = await flexFocusApiClient.post<IMutationResponse>(
        EOrderEndPoint.CREATE_ORDERHEAD,
        orderhead
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  updateOrderhead: async (
    id: number,
    orderhead: Partial<IOrderheadInput>
  ): Promise<IMutationResponse> => {
    try {
      const { data } = await flexFocusApiClient.patch<IMutationResponse>(
        EOrderEndPoint.UPDATE_ORDERHEAD + id,
        orderhead
      );
      return data;
    } catch (error: any) {
      if (Array.isArray(error.message)) {
        error.message.map((item: string) =>
          toast.error(item || "Error on updating order")
        );
      } else {
        toast.error(error.message.toString() || "Error on updating order");
      }
      throw new Error(throwError(error));
    }
  },
  addYarnDetails: async (
    id: number,
    yarnDetails: IYarnDetailsInput
  ): Promise<IMutationResponse> => {
    try {
      const { data } = await flexFocusApiClient.post<IMutationResponse>(
        EOrderEndPoint.ADD_YARN_DETAILS + id,
        yarnDetails
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  updateYarnDetails: async (
    id: number,
    yarnDetails: Partial<IYarnDetailsInput>
  ): Promise<IMutationResponse> => {
    try {
      const { data } = await flexFocusApiClient.patch<IMutationResponse>(
        EOrderEndPoint.UPDATE_YARN_DETAILS + id,
        yarnDetails
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  createStyleAccessory: async (
    style_id: number,
    accessory: IStyleAccessoryInput
  ): Promise<IMutationStyleAccResponse> => {
    try {
      const { data } = await flexFocusApiClient.post<IMutationStyleAccResponse>(
        EOrderEndPoint.CREATE_STYLE_ACCESSORY + style_id,
        accessory
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getStyleAccById: async (
    style_id: number
  ): Promise<IStyleAccessoryResponse<IStyleAccessory>> => {
    try {
      const { data } = await flexFocusApiClient.get<
        IStyleAccessoryResponse<IStyleAccessory>
      >(EOrderEndPoint.STYLE_ACCESSORY + "/" + style_id);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  updateStyleAccessory: async (
    style_accessory_id: number,
    accessory: IStyleAccessoryInput
  ): Promise<IMutationStyleAccResponse> => {
    try {
      const { data } =
        await flexFocusApiClient.patch<IMutationStyleAccResponse>(
          EOrderEndPoint.UPDATE_STYLE_ACCESSORY + style_accessory_id,
          accessory
        );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  deleteStyleAccessory: async (
    style_accessory_id: number
  ): Promise<IMutationStyleAccResponse> => {
    try {
      const { data } =
        await flexFocusApiClient.delete<IMutationStyleAccResponse>(
          EOrderEndPoint.DELETE_STYLE_ACCESSORY + style_accessory_id
        );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getImagesByStyleId: async (
    style_id: number
  ): Promise<IStylePhotoResponse> => {
    try {
      const { data } = await flexFocusApiClient.get<IStylePhotoResponse>(
        EOrderEndPoint.STYLE_PHOTOS + "/" + style_id
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  createStyleImage: async (
    style_id: number,
    photos: FormData
  ): Promise<IStylePhotoResponse> => {
    try {
      const { data } = await flexFocusApiClient.post<IStylePhotoResponse>(
        `${EOrderEndPoint.CREATE_STYLE_PHOTOS}${style_id}`,
        photos,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
    deleteStyleImage: async (
    style_id: number,
    photo_id: string
  ): Promise<{ message: string }> => {
    try {
      const { data } = await flexFocusApiClient.delete<{ message: string }>(
        `${EOrderEndPoint.DELETE_STYLE_PHOTO}${style_id}/${photo_id}`
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getTimelineByStyleId: async (
    style_id: number
  ): Promise<IResponse<ITimeline>> => {
    try {
      const { data } = await flexFocusApiClient.get<IResponse<ITimeline>>(
        EOrderEndPoint.STYLE_TIMELINE + style_id
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  createTimeline: async (
    style_id: number,
    timeline: ITimelineInput
  ): Promise<ITimelineCreateResponse> => {
    try {
      const { data } = await flexFocusApiClient.post<ITimelineCreateResponse>(
        `${EOrderEndPoint.CREATE_TIMELINE}${style_id}`,
        timeline
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  uploadTimeline: async (
    style_id: number,
    document_data: FormData
  ): Promise<ITimelineCreateResponse> => {
    try {
      const { data } = await flexFocusApiClient.post<ITimelineCreateResponse>(
        `${EOrderEndPoint.UPLOAD_TIMELINE}${style_id}`,
        document_data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  orderfetchLogFile:async(orderCode: string):Promise<any>=>{
    try{
      const response= await flexFocusApiClient.get(
        `${EOrderEndPoint.TRACKING_LOG}${orderCode}`,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data
    }catch (error) {
      throw new Error(throwError(error));
    }

  },
  styleFetchLogFile: async (styleId: number): Promise<any> => {
    try {
      const response = await flexFocusApiClient.get(
        `${EOrderEndPoint.STYLE_TRACKING_LOG}${styleId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getSampleRef:async(sampleref:string):Promise<IStandardResponse<Array<ISampleRef>>>=>{
    const controller = new AbortController();
    const timeout = 30_000;
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);
     try {
          const { data } = await flexFocusApiClient.get<
            IStandardResponse<Array<ISampleRef>>
          >(`${EOrderEndPoint.SEARCH_SAMPLEREF}${sampleref}`, {
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          return data;
        } catch (error: any) {
          clearTimeout(timeoutId);
          if (error.name === "AbortError") {
            console.log("Request timed out after 30 seconds");
            throw new Error("Request timed out after 30 seconds");
          }
    
          console.log({ error });
    
          throw new Error(error);
        }

  },
  getPDF_OC:async(id:number):Promise<any>=>{
    try{
      const response= await flexFocusApiClient.get(
        `${EOrderEndPoint.PDF_OC}${id}`,
        {
          headers: {
            "Content-Type": "application/pdf",
          },
        }
      );
      return response.data
    }catch (error) {
      throw new Error(throwError(error));
    }
  },
  ConfirmOrder: async (
    id: number,
  ): Promise<IMutationResponse> => {
    try {
      const { data } = await flexFocusApiClient.get<IMutationResponse>(
        EOrderEndPoint.CONFIRM_ORDER + "/" + id,
      );
      toast.success("Order updated successfully");
      return data;
    } catch (error: any) {
      if (Array.isArray(error.message)) {
        error.message.map((item: string) =>
          toast.error(item || "Error on updating order")
        );
      } else {
        toast.error(error.message.toString() || "Error on updating order");
      }
      throw new Error(throwError(error));
    }
  },
  ReopenOrder:async(id: number): Promise<IMutationResponse>=>{
    try {
      const { data } = await flexFocusApiClient.get<IMutationResponse>(
        EOrderEndPoint.REOPEN_ORDER + "/" + id,
      );
      toast.success(" Order has been re-opened");
      return data;
    } catch (error: any) {
      if (Array.isArray(error.message)) {
        error.message.map((item: string) =>
          toast.error(item || "Error on re-openning order")
        );
      } else {
        toast.error(error.message.toString() || "Error on re-openning order");
      }
      throw new Error(throwError(error));
    }
  },
  DeleteOrder: async (id: number, type?: string): Promise<IMutationResponse> => {
    try {
    const url =
      EOrderEndPoint.DELETE_ORDER +
      "/" +
      id +
      (type ? `?type=${type}` : "");

    const { data } = await flexFocusApiClient.delete<IMutationResponse>(url);

    return data;
  } catch (error: any) {
    throw new Error(error?.message || "Error deleting order");
  }
  },
  GetOrderTimeline:async(order_code:string):Promise<IObjectDataResponse<TimelineData>>=>{
    try {
    const url =
      EOrderEndPoint.ORDER_TIMELINE +
      "/" +
      order_code ;

    const { data } = await flexFocusApiClient.get< IObjectDataResponse<TimelineData>>(url);

    return data;
  } catch (error: any) {
    throw new Error(error?.message || "Error getting timeline order");
  }

  },
  
  getAllYarnCompositions: async () => {
    try {
      const { data } = await flexFocusApiClient.get(`yarncompos/yarn_composition`);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  
  getOneYarnComposition: async (id: number) => {
    try {
      const { data } = await flexFocusApiClient.get(`yarncompos/yarn_composition/${id}`);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  
  createYarnComposition: async (body: ICreateYarnComposition) => {
    try {
      const { data } = await flexFocusApiClient.post(`yarncompos/yarn_composition`, body);
      toast.success("Yarn composition created successfully.");
      return data;
    } catch (error: any) {
      toast.error(error?.message ?? "An unexpected error occurred. Please try again.");
    }
  },
  
  updateYarnComposition: async (id: number, body: IUpdateYarnComposition) => {
    try {
      const { data } = await flexFocusApiClient.put(`yarncompos/yarn_composition/${id}`, body);
      toast.success("Yarn composition updated successfully."); 
      return data;
    }  catch (error: any) {
      toast.error(error?.message ?? "An unexpected error occurred. Please try again.");
    }
  },
  
  deleteYarnComposition: async (id: number) => {
    try {
      const { data } = await flexFocusApiClient.delete(`yarncompos/yarn_composition/${id}`);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  getAllYarnCounts: async () => {
    try {
      const { data } = await flexFocusApiClient.get(`yarncount/yarn_count`);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  
  getOneYarnCount: async (id: number) => {
    try {
      const { data } = await flexFocusApiClient.get(`yarncount/yarn_count/${id}`);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  
  createYarnCount: async (body: ICreateYarnComposition) => {
    try {
      const { data } = await flexFocusApiClient.post(`yarncount/yarn_count`, body);
      toast.success("Yarn count created successfully.");
      return data;
    }  catch (error: any) {
      toast.error(error?.message ?? "An unexpected error occurred. Please try again.");
    }
  },
  
  updateYarnCount: async (id: number, body: IUpdateYarnComposition) => {
    try {
      const { data } = await flexFocusApiClient.put(`yarncount/yarn_count/${id}`, body);
      toast.success("Yarn count updated successfully."); 
      return data;
    }  catch (error: any) {
      toast.error(error?.message ?? "An unexpected error occurred. Please try again.");
    }
  },
  
  deleteYarnCount: async (id: number) => {
    try {
      const { data } = await flexFocusApiClient.delete(`yarncount/yarn_count/${id}`);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  // uploadExcelImport: async (
  //   type: EExcelImportType,
  //   file: FormData
  // ): Promise<IStandardResponse<any>> => {
  //     try {
  //         const { data } = await flexFocusApiClient.post(
  //             EOrderEndPoint.EXCEL_IMPORT_UPLOAD,
  //             file, // 'type' doit déjà être ajouté au FormData avant l'appel
  //             {
  //                 headers: {
  //                     "Content-Type": "multipart/form-data",
  //                 },
  //             }
  //         );

  //         toast.success("Import successful.");

  //         return data;
  //     } catch (error: any) {
  //         toast.error(
  //             error?.error?.message || "Import failed."
  //         );
  //         throw error;
  //     }
  // },

  // getExcelImportProgress: async (type: EExcelImportType): Promise<any> => {
  //     try {
  //         const { data } = await flexFocusApiClient.get(EOrderEndPoint.EXCEL_IMPORT_PROGRESS, {
  //             params: { type },
  //         });
  //         return data;
  //     } catch (error) {
  //         throw new Error(throwError(error));
  //     }
  // },

  uploadExcelImport: async (
    type: EExcelImportType,
    file: FormData
  ): Promise<IStandardResponse<any>> => {
      try {
          const { data } = await flexFocusApiClient.post(
              EOrderEndPoint.EXCEL_IMPORT_UPLOAD,
              file, // 'type' et 'jobId' déjà ajoutés au FormData avant l'appel
              {
                  headers: {
                      "Content-Type": "multipart/form-data",
                  },
              }
          );
          return data;
      } catch (error: any) {
          // toast.error(
          //     error?.error?.message || "Import failed."
          // );
          throw error;
      }
  },

  getExcelImportProgress: async (type: EExcelImportType, jobId: string): Promise<any> => {
      try {
          const { data } = await flexFocusApiClient.get(EOrderEndPoint.EXCEL_IMPORT_PROGRESS, {
              params: { type, jobId },
          });
          return data;
      } catch (error) {
          throw new Error(throwError(error));
      }
  },
};
